"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { ArrowRight, Menu, X, MessageCircle, Mail, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerContent, DrawerTrigger, DrawerTitle, DrawerHeader, DrawerClose } from "@/components/ui/drawer";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem data-index={i} key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.label.toLowerCase() === "about" ? "/About" : item.label.toLowerCase() === "services" ? "/HotelRevenueManagementConsultingPage" : item.label.toLowerCase() === "dynamic pricing" ? "/DynamicPricingForecastingServices2Page" : item.label.toLowerCase() === "channel manager setup" ? "/ChannelManagerSetupDistributionStrategyPage" : item.label.toLowerCase() === "ota listing & optimization" ? "/OTAListingOptimizationServices2Page" : item.label.toLowerCase() === "case studies" ? "/CaseStudiesSuccessStoriesPage" : item.href || "#"} className="relative text-[#0B4D9C] text-sm font-sans font-medium tracking-wide hover:bg-transparent focus:bg-transparent px-0 py-2 group" style={{
            letterSpacing: "0.02em"
          }}>
                <span>{item.label}</span>
                <span className="absolute bottom-0 left-0 h-px w-0 bg-secondary transition-all duration-[250ms] ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-48 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-44 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="relative flex flex-col gap-8 px-8 py-4">
      <span className="absolute left-8 top-4 bottom-4 w-px bg-secondary" aria-hidden="true" />
      <div className="flex flex-col gap-8 pl-6">
        {menuItems.map((item, i) => <Link data-index={i} key={item.id} to={item.label.toLowerCase() === "about" ? "/About" : item.label.toLowerCase() === "services" ? "/HotelRevenueManagementConsultingPage" : item.label.toLowerCase() === "dynamic pricing" ? "/DynamicPricingForecastingServices2Page" : item.label.toLowerCase() === "channel manager setup" ? "/ChannelManagerSetupDistributionStrategyPage" : item.label.toLowerCase() === "ota listing & optimization" ? "/OTAListingOptimizationServices2Page" : item.label.toLowerCase() === "case studies" ? "/CaseStudiesSuccessStoriesPage" : item.href || "#"} onClick={onClose} className="font-default text-3xl text-foreground hover:text-secondary transition-colors duration-200">
            {item.label}
          </Link>)}
      </div>
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const dismissBanner = () => {
    setShowBanner(false);
    setTimeout(() => {
      setShowBanner(true);
    }, 30 * 60 * 1000);
  };
  return <>
      {showBanner && <div className="sticky top-0 z-[60] w-full bg-secondary text-white py-2 px-4 text-center flex items-center justify-center gap-4 shadow-md">
          <span className="text-sm font-medium">Claim your Free Revenue Audit today!</span>
          <Button asChild size="sm" variant="outline" className="bg-white text-secondary border-white hover:bg-white/90 h-7 px-3 text-xs">
            <Link to="#">Get Audit Now</Link>
          </Button>
          <Button variant="ghost" size="icon" onClick={dismissBanner} className="absolute right-4 text-white hover:bg-white/20 size-8" aria-label="Dismiss banner">
            <X className="size-4" />
          </Button>
        </div>}

      <section data-section-id="749" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 ease-out ${scrolled ? "bg-[#0B4D9C] text-white border-b border-white/10" : "bg-white text-[#0B4D9C] border-b border-border/50"}`}>
        <div className="mx-auto flex items-center justify-between px-6 sm:px-8 lg:px-12 transition-all duration-300 ease-out h-20">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="HotelRevBaBa home" className="flex items-center group">
              <span className="relative inline-flex items-center">
                <WvcLogo className="h-7 w-auto" />
                <span className="ml-1 inline-block h-1.5 w-1.5 rounded-lg bg-secondary transition-transform duration-300 group-hover:scale-125" />
              </span>
            </Link>
            <Link to="https://sunilsingh.co.in/" newTab className="text-sm font-medium text-muted-foreground hover:text-secondary transition-colors">
              Sunil Singh
            </Link>
          </div>

          <div className="hidden lg:flex items-center justify-center flex-1">
            <MenuProvider menu_id="42" className="header-menu">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="hidden lg:flex items-center">
            <Button asChild className="rounded-lg bg-[#0B4D9C] text-white hover:bg-[#0B4D9C]/90 px-6 py-2 h-auto text-sm font-sans font-medium tracking-wide group shadow-md">
              <Link to="/ContactPage">
                <span style={{
                letterSpacing: "0.02em"
              }}>Book Consultation</span>
                <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>

          <div className="flex lg:hidden items-center">
            <SafeHydrate><Drawer open={mobileOpen} onOpenChange={setMobileOpen} direction="right">
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-transparent">
                  <Menu className="h-[18px] w-[18px]" strokeWidth={1.5} />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="bg-background w-full sm:max-w-sm border-l border-secondary/20">
                <DrawerHeader className="flex items-center justify-between border-b border-border/50 px-6 py-5">
                  <DrawerTitle asChild>
                    <Link to="/" onClick={() => setMobileOpen(false)} aria-label="HotelRevBaBa home" className="inline-flex items-center">
                      <WvcLogo className="h-7 w-auto" />
                      <span className="ml-1 inline-block h-1.5 w-1.5 rounded-lg bg-secondary" />
                    </Link>
                  </DrawerTitle>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon" aria-label="Close menu" className="text-foreground hover:bg-transparent">
                      <X className="h-[18px] w-[18px]" strokeWidth={1.5} />
                    </Button>
                  </DrawerClose>
                </DrawerHeader>

                <div className="flex-1 overflow-y-auto py-8">
                  <MenuProvider menu_id="42" className="header-menu-mobile">
                    <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="border-t border-border/50 px-6 py-6">
                  <Button asChild className="w-full rounded-lg bg-[#0B4D9C] text-white hover:bg-[#0B4D9C]/90 h-11 text-sm font-sans font-medium tracking-wide group shadow-md">
                    <Link to="/ContactPage" onClick={() => setMobileOpen(false)}>
                      <span style={{
                        letterSpacing: "0.02em"
                      }}>Book Consultation</span>
                      <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </DrawerContent>
            </Drawer></SafeHydrate>
          </div>
        </div>
      </section>

      <div className="fixed bottom-6 right-6 z-[60] flex flex-col gap-3">
        <Button asChild title="Chat with us on WhatsApp" className="size-14 rounded-lg bg-[#25D366] text-white shadow-2xl hover:bg-[#25D366]/90 transition-all hover:scale-110 p-0">
          <Link to="https://wa.me/917972195929?text=Hi%20Sunil,%20I'd%20like%20a%20free%20revenue%20audit" newTab aria-label="Chat with us on WhatsApp">
            <MessageCircle className="size-7" />
          </Link>
        </Button>
        
        <div className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-lg border border-border">
          <Link to="mailto:info@hotelrevbaba.com" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary">
            <Mail className="size-4" />
            <span>Email</span>
          </Link>
          <Link to="tel:+917972195929" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary">
            <Phone className="size-4" />
            <span>Call</span>
          </Link>
        </div>
      </div>

      <Button asChild className="fixed bottom-24 right-6 z-50 hidden md:flex bg-[#0B4D9C] text-white px-4 py-2 rounded-lg shadow-lg hover:bg-[#0B4D9C]/90 transition-colors">
        <Link to="/ContactPage">
          Schedule Free Audit
        </Link>
      </Button>
    </>;
}